'use client';

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Route } from 'next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useToast } from '@unreserved/hooks';
import { useAuthStore } from '@/stores/auth-store';
import { handleHttpError } from '@unreserved/utils';
import { getAdminAuthApi, getAdminCustomersApi } from '@/lib/utils/admin-sdk';
import { ADMIN_ROUTES } from '@/app/_libs/constants/routes';
import type {
  AuthResponseDto,
  SocialAuthDtoProviderEnum,
  SocialAuthUrlResponseDto,
  UserProfileDto,
} from '@unreserved/sdk';

type LoginVariables = {
  email: string;
  password: string;
};

type SocialAuthVariables = {
  provider: SocialAuthDtoProviderEnum;
};

export function useAuth() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const setUser = useAuthStore((state) => state.setUser);
  const clearUser = useAuthStore((state) => state.clearUser);

  const loginMutation = useMutation<AuthResponseDto, Error, LoginVariables>({
    mutationKey: ['admin-login'],
    mutationFn: async (variables) => {
      const adminAuthApi = getAdminAuthApi();
      const response = await adminAuthApi.authControllerSigninV1({
        signinDto: {
          email: variables.email,
          password: variables.password,
        },
      });

      if (response.status !== 'Success') return response;

      try {
        const adminCustomersApi = getAdminCustomersApi();
        const profile = await adminCustomersApi.userControllerGetProfileV1();
        setUser(profile.data as UserProfileDto);
      } catch {
        toast.error('Failed to load profile');
      }

      const redirectTo = searchParams?.get('redirectTo');
      const safeRedirectPath =
        redirectTo && redirectTo.startsWith('/')
          ? redirectTo
          : '/dashboard';
      router.replace(safeRedirectPath as Route);

      return response;
    },
    onSuccess: (response) => {
      if (response.status !== 'Success') return;
      toast.success('Login successful', {
        description: response.message ?? 'Logged in successfully',
      });
    },
    onError: (error: Error & { response?: Response }) => {
      handleHttpError(error, 'Login failed', toast);
    },
  });

  const logoutMutation = useMutation<void, Error, string>({
    mutationKey: ['admin-logout'],
    mutationFn: async (deviceId: string) => {
      const adminAuthApi = getAdminAuthApi();
      await adminAuthApi.authControllerLogoutV1({
        logoutDto: {
          deviceId,
        },
      });
    },
    onSuccess: () => {
      clearUser();
      toast.success('Logged out successfully', {
        description: 'You have been logged out successfully',
      });
      router.push(ADMIN_ROUTES.LOGIN as Route);
    },
    onError: (error: Error & { response?: Response }) => {
      handleHttpError(error, 'Failed to logout', toast);
    },
  });

  const [isRedirecting, setIsRedirecting] = useState(false);

  const socialAuthUrlMutation = useMutation<
    SocialAuthUrlResponseDto['data'] | undefined,
    Error,
    SocialAuthVariables
  >({
    mutationKey: ['admin-social-auth-url'],
    mutationFn: async (variables) => {
      const adminAuthApi = getAdminAuthApi();
      const response = await adminAuthApi.authControllerGetSocialAuthUrlV1Raw({
        socialAuthDto: {
          provider: variables.provider,
        },
      });

      const body = await response.value();

      if (body?.status !== 'Success' || !body.data?.authorizationUrl) {
        throw new Error(body?.message ?? 'Failed to generate social login URL');
      }

      return body.data;
    },
    onSuccess: (data) => {
      if (!data?.authorizationUrl) return;
      if (typeof window !== 'undefined') {
        setIsRedirecting(true);
        window.location.href = data.authorizationUrl;
      }
    },
    // Error toast is shown by GoogleLoginButton with a user-friendly message
  });

  return {
    // login
    login: loginMutation.mutateAsync,
    loginResult: loginMutation,

    // logout
    logout: logoutMutation.mutateAsync,
    logoutResult: logoutMutation,

    // social login
    getSocialAuthUrl: socialAuthUrlMutation.mutateAsync,
    socialAuthUrlResult: socialAuthUrlMutation,
    isSocialAuthRedirecting: isRedirecting,
  };
}
